import { useCallback, useEffect, useMemo } from 'react';
import { Table } from '@carboledger/ui';
import { FileUp, Plus } from '@carboledger/ui/icons';
import { buildTableLabels } from '@/config/tableLabels';
import { useTableState, useTranslations } from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';
import supplierEmptyIllustration from '@/assets/illustrations/supplier-empty.svg';
import { useGetAccountSuppliersQuery } from '../api/suppliersApi';
import { buildSuppliersColumns } from '../constants/suppliersColumns';

const getSupplierRowId = (row) => row.id;

/**
 * Props:
 *   onAdd       header "Add supplier" handler, also the empty state's CTA
 *   onEdit      row menu "Edit" handler — the only row action, since the backend has no
 *               DELETE /suppliers/:id
 *   onTotalChange  (total: number) => void — reports the unfiltered supplier count upward,
 *                  so the page header can show its "Import suppliers sites" button only
 *                  once at least one supplier exists (matches the Figma empty vs. populated states)
 */
export const SuppliersTable = ({ onAdd, onEdit, onTotalChange }) => {
  const t = useTranslations();
  const table = useTableState();

  const { data, isFetching } = useGetAccountSuppliersQuery(table.queryParams);
  const rows = data?.rows ?? EMPTY_LIST;
  const total = data?.totalCount ?? 0;

  useEffect(() => {
    if (!table.search && !table.hasActiveFilters) onTotalChange?.(total);
  }, [total, table.search, table.hasActiveFilters, onTotalChange]);

  const handleSearchChange = useCallback(
    (value) => {
      table.setSearch(value);
      table.setPage(1);
    },
    [table],
  );

  const handleSortChange = useCallback(
    (next) => {
      table.setSort(next ? { field: next.id, direction: next.dir } : null);
      table.setPage(1);
    },
    [table],
  );

  const defaultSort = useMemo(
    () => (table.sort ? { id: table.sort.field, dir: table.sort.direction } : null),
    [table.sort],
  );

  const columns = useMemo(
    () => buildSuppliersColumns(t, { serialOffset: (table.page - 1) * table.pageSize }),
    [t, table.page, table.pageSize],
  );

  const rowMenuActions = useMemo(
    () => [{ id: 'edit', label: t('COMMON_EDIT'), onClick: onEdit }],
    [t, onEdit],
  );

  const emptyState = useMemo(
    () => ({
      image: <img src={supplierEmptyIllustration} alt="" width={200} height={200} />,
      title: t('SUPPLIERS_EMPTY_TITLE'),
      description: t('SUPPLIERS_EMPTY_DESCRIPTION'),
      primaryLabel: t('SUPPLIERS_ADD'),
      onPrimaryClick: onAdd,
      primaryIcon: <Plus />,
      secondaryLabel: t('SUPPLIERS_IMPORT'),
      secondaryIcon: <FileUp />,
    }),
    [t, onAdd],
  );

  const noResults = useMemo(
    () => ({
      title: t('SUPPLIERS_NO_RESULTS_TITLE'),
      description: t('COMMON_NO_RESULTS_DESCRIPTION'),
    }),
    [t],
  );

  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  return (
    <Table
      columns={columns}
      data={rows}
      total={total}
      loading={isFetching}
      getRowId={getSupplierRowId}
      defaultSort={defaultSort}
      onSortChange={handleSortChange}
      searchable
      onSearchChange={handleSearchChange}
      onPageChange={table.setPage}
      onPageSizeChange={table.setPageSize}
      rowMenuActions={rowMenuActions}
      actionsHeader={t('FIELD_ACTION')}
      emptyState={emptyState}
      noResults={noResults}
      labels={tableLabels}
    />
  );
};
